import { CommonModule } from '@angular/common';
import { Component, Injector, OnInit, OnDestroy } from '@angular/core';
import { RouterModule, Router } from '@angular/router';
import {DEBOUNCE_TIME, extractErrorMessage, goToNextPage} from 'src/app/common/common';
import { PowerTypes } from 'src/app/model/powerTypes.model';
import { AuthService } from 'src/app/services/auth.service';
import {ApiService, CustomHttpParams} from 'src/app/services/api.service';
import { NgxPaginationModule } from 'ngx-pagination';
import { FormsModule } from '@angular/forms';
import {PaginationClass, PaginationModel} from "../../../../common/pagination";
import {LoaderComponent} from "../../../common/loader/loader.component";
import { BehaviorSubject, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-powertype-index',
  standalone: true,
  imports: [RouterModule, CommonModule, NgxPaginationModule, FormsModule, LoaderComponent],
  templateUrl: './powertype-index.component.html',
  styleUrl: './powertype-index.component.scss'
})
export class PowertypeIndexComponent implements OnInit, OnDestroy {
  // Search fields for all columns
  public itemDescriptionSearch: string = '';
  public itemUnitSearch: string = '';
  public siScaleCodeSearch: string = '';
  public hertzSearch: string = '';
  public voltageSearch: string = '';
  public acSearch: string = '';
  public updatedBySearch: string = '';

  // Debounce search using BehaviorSubject
  private searchParams$ = new BehaviorSubject<any>({
    itemDescription: '',
    itemUnit: '',
    siScaleCode: '',
    hertz: '',
    voltage: '',
    ac: '',
    updatedBy: ''
  });
  private unsubscribe = new Subject<void>();

  protected readonly paginationId = 'powertypesPagination';
  private pagination = new PaginationClass(this.paginationId);
  protected paginationModel: PaginationModel = this.pagination.getPaginationModel();

  public isAdmin: boolean = false;
  public isLoading: boolean = false;
  public displayedPowerTypes: PowerTypes[] = [];
  public error: string | null = null;
  public router: Router;

  constructor(private injector: Injector, private authService: AuthService, private powerTypeService: ApiService) {
    this.router = injector.get(Router);
  }

  ngOnInit(): void {
    this.isAdmin = this.authService.isAdmin();
    this.paginationModel = this.pagination.resetPagination();

    // Setup debounced search that tracks actual parameter changes
    this.searchParams$.pipe(
      debounceTime(DEBOUNCE_TIME),
      distinctUntilChanged((prev, curr) => JSON.stringify(prev) === JSON.stringify(curr)),
      takeUntil(this.unsubscribe)
    ).subscribe(() => {
      this.loadPowerTypes();
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  buildSearchParams(): CustomHttpParams {
    const params: CustomHttpParams = {
      'depth': 2,
      'limit': this.pagination.itemsPerPage,
      'offset': this.pagination.offset,
    };

    // Add search parameters if they exist
    if (this.itemDescriptionSearch) {
      params['itemLabel'] = this.itemDescriptionSearch.trim();
    }
    if (this.itemUnitSearch) {
      params['itemUnit'] = this.itemUnitSearch.trim();
    }
    if (this.siScaleCodeSearch) {
      params['siScaleCode'] = this.siScaleCodeSearch.trim();
    }
    if (this.hertzSearch) {
      params['hertz'] = this.hertzSearch.trim();
    }
    if (this.voltageSearch) {
      params['voltage'] = this.voltageSearch.trim();
    }
    if (this.acSearch) {
      params['ac'] = this.acSearch.trim();
    }
    if (this.updatedBySearch) {
      params['updated_by'] = this.updatedBySearch.trim();
    }

    return params;
  }

  triggerSearch() {
    this.paginationModel = this.pagination.resetPagination();

    // Update the search parameters subject with current values
    this.searchParams$.next({
      itemDescription: this.itemDescriptionSearch,
      itemUnit: this.itemUnitSearch,
      siScaleCode: this.siScaleCodeSearch,
      hertz: this.hertzSearch,
      voltage: this.voltageSearch,
      ac: this.acSearch,
      updatedBy: this.updatedBySearch
    });
  }

  loadPowerTypes() {
    this.isLoading = true;
    this.error = null;

    // Build parameters using the centralized method
    const queryParams = this.buildSearchParams();

    this.powerTypeService.getPowerType(queryParams)
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (response: any) => {
          this.displayedPowerTypes = response.map((pt: PowerTypes) => pt);
          this.paginationModel = this.pagination.updateTotalItems(response.length);
          this.isLoading = false;
        },
        error: (err) => {
          console.error('Error loading power types:', err);
          this.error = extractErrorMessage(err, "Failed to load power types.");
          this.isLoading = false;
        }
      });
  }

  handlePageChange(newPage: number) {
    this.paginationModel = this.pagination.changePage(newPage);
    this.loadPowerTypes();
  }

  nextPage(event: any, url: string, router: Router) {
    return goToNextPage(event, url, router);
  }

  delete(powerType: any) {
    if (confirm('Are you sure you want to delete this powertype?')) {
      this.powerTypeService.deletePowerTypes(powerType.itemDescription)
        .pipe(takeUntil(this.unsubscribe))
        .subscribe({
          next: () => {
            this.loadPowerTypes(); // Reload power types after successful deletion
          },
          error: (err) => {
            console.error('Error deleting power type:', err);
            this.error = extractErrorMessage(err, "Failed to delete power type.");
          }
        });
    }
  }
}
